// src/components/home/MonthlyAnalytics/DailySalesModal.jsx
import React, { useState, useEffect } from 'react';
import { monthlySalesApi } from '../../../../api/monthlysales/monthlySalesApi';
import styles from './DailySalesModal.module.css';
import {
    Chart as ChartJS, CategoryScale, LinearScale,
    BarElement, Title, Tooltip, Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const fmt = (n) => (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const DailySalesModal = ({ show, onHide, month, year, monthName, type, filterValue, categoryFilter, rowData }) => {
    const [loading,   setLoading]   = useState(true);
    const [dailyData, setDailyData] = useState([]);
    const [error,     setError]     = useState(null);

    useEffect(() => {
        if (!show) return;

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const payload = { type, month, year };
                if (type === 'category'   && filterValue) payload.category    = filterValue;
                if (type === 'customer'   && filterValue) payload.customer     = filterValue;
                if (type === 'salesperson'&& filterValue) payload.salesperson  = filterValue;
                if (categoryFilter && type !== 'category') payload.category    = categoryFilter;

                const res = await monthlySalesApi.getDailySales(payload);
                setDailyData(res.dailyData || []);
            } catch (err) {
                console.error('Daily sales error:', err);
                setError(err.message);
                setDailyData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [show, month, year, type, filterValue, categoryFilter]);

    const generateAllDays = () => {
        const daysInMonth = new Date(year, month, 0).getDate();
        return Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const found = dailyData.find(d => d.date === dateStr);
            return {
                date: dateStr,
                sales:     Math.round(found?.sales     || 0),
                lineItems: Math.round(found?.lineItems || 0),
            };
        });
    };

    const allDays = generateAllDays();
    const totalSales = allDays.reduce((s, d) => s + d.sales, 0);
    const totalLines = allDays.reduce((s, d) => s + d.lineItems, 0);

    const chartData = {
        labels: allDays.map(d => d.date.split('-')[2]),
        datasets: [{
            label: 'Daily Sales',
            data: allDays.map(d => d.sales),
            lineItemsData: allDays.map(d => d.lineItems),
            backgroundColor: '#1864AB',
            borderColor: '#1864AB',
            borderWidth: 1,
            borderRadius: 3,
        }]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: (ctx) => `${monthName} ${year} — Day ${ctx[0].label}`,
                    label: (ctx) => {
                        const d = allDays[ctx.dataIndex];
                        return [
                            `Line Items: ${fmt(d.lineItems)}`,
                            `Sales: ₹${fmt(d.sales)}`
                        ];
                    }
                }
            }
        },
        scales: {
            x: { title: { display: true, text: 'Day of Month' }, grid: { display: false } },
            y: {
                title: { display: true, text: 'Sales (₹)' },
                ticks: { callback: (v) => '₹' + fmt(v) }
            }
        }
    };

    const getTitle = () => {
        let t = `${monthName} ${year} — Daily Breakdown`;
        if (filterValue) t += ` · ${filterValue}`;
        if (categoryFilter && type !== 'category') t += ` (${categoryFilter})`;
        return t;
    };

    if (!show) return null;

    return (
        <div className={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onHide(); }}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <div>
                        <h3 className={styles.title}>{getTitle()}</h3>
                        <div className={styles.badges}>
                            <span className={styles.badgeBlue}>Line Items: {fmt(totalLines)}</span>
                            <span className={styles.badgeGreen}>Sales: ₹{fmt(totalSales)}</span>
                        </div>
                    </div>
                    <button className={styles.closeBtn} onClick={onHide}>✕</button>
                </div>

                <div className={styles.body}>
                    {loading && (
                        <div className={styles.loading}>
                            <span className={styles.spinner} />
                            Loading daily sales data...
                        </div>
                    )}
                    {error && (
                        <div className={styles.error}>Error: {error}</div>
                    )}
                    {!loading && !error && (
                        <div style={{ height: '420px' }}>
                            <Bar data={chartData} options={chartOptions} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DailySalesModal;